import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { useHeaderGeneration } from '@/hooks/text-generation';
import { researchClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import type { ResearchRequest } from '@/integrations/perplexity';
import { RelatedQuestionGenerationOptions } from './types';

export const usePrivateResearchQuestionGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { generateHeader } = useHeaderGeneration();
  const queryClient = useQueryClient();

  const generateResearchFromQuestion = async (options: RelatedQuestionGenerationOptions): Promise<string | null> => {
    setIsGenerating(true);
    setError(null);

    try {
      console.log('[usePrivateResearchQuestionGeneration] Starting private research generation for question:', options.question);

      // Step 1: Build research request for the research client with original article context
      // Get the original research title to provide context for the related question
      const { data: { user: currentUser }, error: userAuthError } = await supabase.auth.getUser();
      if (userAuthError || !currentUser) {
        throw new Error('User not authenticated');
      }

      // Fetch the original research to get its title and workbook_id for context
      let originalResearchTitle = '';
      let originalWorkbookId: string | null = null;
      if (options.currentResearchId) {
        const { data: originalResearch, error: fetchError } = await supabase
          .from('workbook_research')
          .select('title, workbook_id')
          .eq('id', options.currentResearchId)
          .eq('user_id', currentUser.id)
          .single();

        if (!fetchError && originalResearch) {
          originalResearchTitle = originalResearch.title;
          originalWorkbookId = originalResearch.workbook_id;
        }
      }

      // Build enhanced prompt with original article context
      const enhancedPrompt = originalResearchTitle 
        ? `${options.question}. Relate your response to "${originalResearchTitle}"`
        : options.question;

      const researchRequest: ResearchRequest = {
        researchPrompt: enhancedPrompt,
        researchType: options.researchParameters.research_type || 'comprehensive_analysis',
        timeframe: options.researchParameters.timeframe,
        country: options.researchParameters.country,
        sourceType: options.researchParameters.source_type,
        selectedFilters: options.researchParameters.type_specific_filters || {}
      };

      // Call the research client
      let researchContent;
      try {
        researchContent = await researchClient.generateResearch(researchRequest);
      } catch (clientError) {
        console.error('[usePrivateResearchQuestionGeneration] Research client error:', clientError);
        const errorMessage = clientError instanceof IntegrationError 
          ? clientError.message 
          : (clientError instanceof Error ? clientError.message : 'Failed to generate research');
        setError(errorMessage);
        toast({
          title: 'Private research generation failed',
          description: errorMessage,
          variant: 'destructive',
        });
        return null;
      }

      if (!researchContent || !researchContent.content) {
        console.error('[usePrivateResearchQuestionGeneration] No research content returned:', researchContent);
        setError('No research content generated');
        toast({
          title: 'Private research generation failed',
          description: 'No research content was generated from the question',
          variant: 'destructive',
        });
        return null;
      }

      console.log('[usePrivateResearchQuestionGeneration] Research content generated successfully');

      // Step 2: Generate a header for the research
      let title: string;
      try {
        console.log('[usePrivateResearchQuestionGeneration] Generating header for research content...');
        const headerResult = await generateHeader({
          content: researchContent.content || '',
          originalPrompt: options.question,
          context: `Research Type: ${options.researchParameters.research_type || 'comprehensive_analysis'}${options.researchParameters.timeframe ? `, Timeframe: ${options.researchParameters.timeframe}` : ''}${options.researchParameters.country ? `, Country: ${options.researchParameters.country}` : ''}`,
        });

        title = headerResult.header;
        console.log('[usePrivateResearchQuestionGeneration] Generated header:', title);
      } catch (headerError) {
        console.error('[usePrivateResearchQuestionGeneration] Header generation failed, using fallback:', headerError);
        // Fallback to original question if header generation fails
        title = options.question.substring(0, 100);
      }

      // Step 3: Get authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Step 4: Build research parameters (following private research generation pattern)
      const effectiveResearchType = options.researchParameters.research_type || 'comprehensive_analysis';
      const researchParameters = {
        research_type: effectiveResearchType,
        timeframe: options.researchParameters.timeframe || null,
        timeframe_label: researchContent.timeframeLabel || null,
        date_range_start: researchContent.dateRangeStart || null,
        date_range_end: researchContent.dateRangeEnd || null,
        country: options.researchParameters.country || null,
        source_type: options.researchParameters.source_type || null,
        type_specific_filters: options.researchParameters.type_specific_filters || {}
      };

      console.log('[usePrivateResearchQuestionGeneration] Research parameters object:', researchParameters);
      console.log('[usePrivateResearchQuestionGeneration] Saving with workbook_id:', originalWorkbookId, 'from original research:', options.currentResearchId);

      // Step 5: Save the research to workbook_research table with same workbook_id as original
      const { data: savedResearch, error: saveError } = await supabase
        .from('workbook_research')
        .insert({
          user_id: user.id,
          workbook_id: originalWorkbookId, // Inherit workbook_id from original research
          page_id: null, // Not assigned to any specific page
          title,
          content: researchContent.content,
          research_type: effectiveResearchType,
          original_prompt: options.question,
          metadata: researchContent.metadata || {},
          sources: researchContent.sources || [],
          related_questions: researchContent.related_questions || [],
          usage: researchContent.usage || {},
          research_parameters: researchParameters,
          system_prompt: researchContent.system_prompt || null,
          generated_research_count: 0, // Default for new research
        })
        .select()
        .single();

      if (saveError) {
        console.error('[usePrivateResearchQuestionGeneration] Error saving private research:', saveError);
        setError(`Failed to save private research: ${saveError.message}`);
        toast({
          title: 'Private research save failed',
          description: `Failed to save private research: ${saveError.message}`,
          variant: 'destructive',
        });
        return null;
      }

      console.log('[usePrivateResearchQuestionGeneration] Private research saved successfully with ID:', savedResearch.id);
      
      toast({
        title: 'Private research generated',
        description: 'New private research has been generated from your question',
      });

      // Invalidate private research queries to update the UI
      console.log('[usePrivateResearchQuestionGeneration] Private research generation successful, invalidating private research queries');
      
      // CRITICAL FIX: Invalidate the source research item details for immediate related questions update
      if (options.currentResearchId) {
        queryClient.invalidateQueries({
          queryKey: ['private-research-item-details', options.currentResearchId],
          exact: true,
          refetchType: 'all'  // Force immediate refetch for real-time UI updates
        });
        console.log('[usePrivateResearchQuestionGeneration] Invalidated private research item details for:', options.currentResearchId);
      }
      
      // Also invalidate the general item details cache pattern
      queryClient.invalidateQueries({
        queryKey: ['private-research-item-details'],
        exact: false,
        refetchType: 'all'
      });
      
      // CRITICAL FIX: Use refetchType: 'all' instead of 'active' to force immediate refetch
      // The private research page uses complex cache keys: ['private-research-generator', user?.id, debouncedSearchQuery, filterOptions]
      // We need to invalidate ALL variations to ensure immediate UI updates
      
      // 1. Invalidate private research generator queries (main page cache)
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-generator'],
        exact: false,
        refetchType: 'all'  // CHANGED: Force refetch of all cached variations
      });
      
      // 2. Invalidate private research count queries
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-generator-count'],
        exact: false,
        refetchType: 'all'  // CHANGED: Force immediate refetch
      });
      
      // 3. CRITICAL: Invalidate the total user research query that controls hasData/empty state
      queryClient.invalidateQueries({
        queryKey: ['private-research-total'],
        exact: false,
        refetchType: 'all'
      });
      
      // 4. Invalidate universal storage queries for private research
      queryClient.invalidateQueries({
        queryKey: ['universal-storage-items'],
        exact: false,
        refetchType: 'all'
      });
      
      queryClient.invalidateQueries({
        queryKey: ['universal-library-data'],
        exact: false,
        refetchType: 'all'
      });
      
      // 5. Invalidate library counts (for sidebar counters)
      queryClient.invalidateQueries({
        queryKey: ['universal-library-counts'],
        exact: false,
        refetchType: 'all'
      });
      
      // 6. CRITICAL: Invalidate workbook research queries to update project resources tab
      queryClient.invalidateQueries({
        queryKey: ['workbook-research'],
        exact: false,
        refetchType: 'all'
      });
      
      console.log('[usePrivateResearchQuestionGeneration] Query invalidation completed with aggressive refetch');

      return savedResearch.id;
    } catch (err: any) {
      console.error('[usePrivateResearchQuestionGeneration] Error generating private research:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      
      toast({
        title: 'Private research generation failed',
        description: errorMessage,
        variant: 'destructive',
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateResearchFromQuestion,
    isGenerating,
    error,
    clearError: () => setError(null)
  };
}; 